import { Controller } from "@hotwired/stimulus"

// data-controller="scroll-to-bottom"
//   <div data-scroll-to-bottom-target="container">…messages…</div>
//
// Pins the container's scroll position to the bottom on initial load,
// after every Turbo Stream render, and whenever new content is appended
// (MutationObserver). Stops auto-scrolling if the user scrolls up
// manually — they're probably reading something — and resumes once they
// scroll back near the bottom.
export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.userScrolledAway = false
    this.boundOnScroll = this.onScroll.bind(this)
    this.container.addEventListener("scroll", this.boundOnScroll, { passive: true })

    this.observer = new MutationObserver(() => this.maybeScroll())
    this.observer.observe(this.container, { childList: true, subtree: true, characterData: true })

    // Initial scroll — try several timing checkpoints because inside a
    // freshly-mounted iframe the container's height isn't fully resolved
    // until fonts and CSS finish. Whichever fires last wins (they're all
    // idempotent — scrollTop=scrollHeight is a no-op once we're pinned).
    this.scrollToBottom()
    requestAnimationFrame(() => this.scrollToBottom())
    this.boundLoadScroll = () => this.scrollToBottom()
    if (document.readyState !== "complete") {
      window.addEventListener("load", this.boundLoadScroll, { once: true })
    }
  }

  disconnect() {
    this.container.removeEventListener("scroll", this.boundOnScroll)
    this.observer?.disconnect()
  }

  get container() {
    return this.hasContainerTarget ? this.containerTarget : this.element
  }

  onScroll() {
    const c = this.container
    const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight
    this.userScrolledAway = distanceFromBottom > 100
  }

  maybeScroll() {
    if (this.userScrolledAway) return
    this.scrollToBottom()
  }

  scrollToBottom() {
    const c = this.container
    c.scrollTop = c.scrollHeight
  }
}
